import { isPlainObject } from 'lodash-es';

import type { FormDefinition, FormQuestion } from 'modules/forms/types';

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

const mapQuestion = (raw: unknown): FormQuestion | null => {
  if (!isPlainObject(raw)) return null;
  const q = raw as Record<string, unknown>;
  // Only types this build can render. A newer form with a type we don't know is dropped whole:
  // a half-rendered form is worse than none.
  if (q.type !== 'open_text') return null;
  if (!isNonEmptyString(q.key) || !isNonEmptyString(q.prompt) || typeof q.maxLength !== 'number') return null;
  return {
    key: q.key,
    type: 'open_text',
    prompt: q.prompt,
    placeholder: isNonEmptyString(q.placeholder) ? q.placeholder : undefined,
    maxLength: q.maxLength,
    dictation: q.dictation === true,
  };
};

/** Validates the form service's definition at the boundary (CLAUDE.md rule 20). */
export const mapFormDefinitionFromApi = (raw: unknown): FormDefinition | null => {
  if (!isPlainObject(raw)) return null;
  const f = raw as Record<string, unknown>;
  if (!isNonEmptyString(f.key) || typeof f.version !== 'number' || !isNonEmptyString(f.title)) return null;
  const subjectType = isPlainObject(f.subject) ? (f.subject as Record<string, unknown>).type : undefined;
  if (!isNonEmptyString(subjectType)) return null;
  if (!Array.isArray(f.questions) || f.questions.length === 0) return null;

  const questions = f.questions.map(mapQuestion);
  if (questions.some((q) => q === null)) return null;

  return {
    key: f.key,
    version: f.version,
    subject: { type: subjectType },
    context: isPlainObject(f.context) ? (f.context as FormDefinition['context']) : {},
    title: f.title,
    subtitle: isNonEmptyString(f.subtitle) ? f.subtitle : undefined,
    questions: questions as FormQuestion[],
    requireAnyAnswer: f.requireAnyAnswer === true,
  };
};
